import React, { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import IconMenu from 'components/IconMenu';
import { ArgumentT } from 'routes/BudgetModule/config/types';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuItem from '@mui/material/MenuItem';
import ModalForm from 'components/Form/ModalForm';
import { capitalize } from 'helpers/helpers';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { red } from '@mui/material/colors';
import ListItemIcon from '@mui/material/ListItemIcon';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import Flex from 'ui/Flex/Flex';
import {
    useDeleteArgumentMutation,
    useDuplicateArgumentMutation,
    useRenameArgumentMutation,
} from '../hooks/arguments';

type Props = {
    argument: ArgumentT;
    resourceSoftwareId: string;
};

const StyLink = styled(Box, {
    name: 'StyLink',
})<ComponentProps<typeof Link>>({
    padding: '1rem 0 1rem 1rem',
});

const StyArgumentCard = styled(Box, {
    name: 'StyArgumentCard',
})({
    backgroundColor: 'white',
    display: 'flex',
    flexFlow: 'row nowrap',
    // one-line and two-line titles have the same height
    minHeight: '4.25rem',
    justifyContent: 'space-between',
    paddingRight: '1rem',
    boxShadow: '0px 2px 2px #eee',
    borderRadius: '4px',
    width: 'max(18rem, 22rem)',
    marginRight: '1rem',
    marginTop: '1rem',
});

const ArgumentCard: React.FC<Props> = ({ argument, resourceSoftwareId }) => {
    const location = useLocation();
    const { id, title } = argument;
    const path = `${location.pathname}/${id}`;
    const { t } = useTranslation();

    const [showEditModal, setShowEditModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const trackingMetadata = {
        argument: argument.title,
    };

    const renameArgumentMutation = useRenameArgumentMutation(
        resourceSoftwareId,
        trackingMetadata,
    );

    const deleteArgumentMutation = useDeleteArgumentMutation(
        resourceSoftwareId,
        trackingMetadata,
    );

    const duplicateArgumentMutation = useDuplicateArgumentMutation(
        resourceSoftwareId,
        trackingMetadata,
    );

    return (
        <StyArgumentCard>
            <StyLink
                component={Link}
                to={path}
                sx={{ display: 'flex', alignItems: 'center', flex: 1 }}
            >
                <Typography variant="body1SemiBold">{title}</Typography>
            </StyLink>
            {/* Without this intermediate flex wrapper, IconMenu would stretch (visible on hover) */}
            <Flex align="center">
                <IconMenu
                    iconElement={<MoreVertIcon fontSize="small" />}
                    data-testid="argument-menu"
                >
                    <MenuItem onClick={() => setShowEditModal(true)}>
                        <ListItemIcon>
                            <EditIcon />
                        </ListItemIcon>
                        {capitalize(t(`common.rename`))}
                    </MenuItem>
                    <MenuItem onClick={() => setShowDuplicateModal(true)}>
                        <ListItemIcon>
                            <FileCopyIcon />
                        </ListItemIcon>
                        {capitalize(t(`common.duplicate`))}
                    </MenuItem>
                    <MenuItem onClick={() => setShowDeleteModal(true)}>
                        <ListItemIcon>
                            {' '}
                            <DeleteIcon />{' '}
                        </ListItemIcon>
                        {capitalize(t(`common.delete`))}
                    </MenuItem>
                </IconMenu>
            </Flex>
            <ModalForm
                show={showEditModal}
                closeModal={() => setShowEditModal(false)}
                submit={(editedTitle: { title: string }) => {
                    renameArgumentMutation.mutate({
                        title: editedTitle.title,
                        id: id.toString(),
                    });
                    setShowEditModal(false);
                }}
                submitText={t('common.save')}
                title={t('budgetProposal.renameArgument')}
                fields={[
                    {
                        key: 'title',
                        name: 'title',
                        label: t('common.title'),
                        type: 'text',
                        value: title,
                        validators: ['required'],
                    },
                ]}
            />
            <ModalForm
                show={showDeleteModal}
                closeModal={() => setShowDeleteModal(false)}
                submit={() => {
                    deleteArgumentMutation.mutate(id.toString());
                    setShowDeleteModal(false);
                }}
                submitText={t('common.delete')}
                title={t('common.deleteConfirm')}
                confirmButtonColor={red[900]}
            />
            <ModalForm
                show={showDuplicateModal}
                closeModal={() => setShowDuplicateModal(false)}
                submit={(newTitle: { title: string }) => {
                    duplicateArgumentMutation.mutate({
                        title: newTitle.title,
                        id: id.toString(),
                    });
                    setShowDuplicateModal(false);
                }}
                submitText={t('common.save')}
                title={t('budgetProposal.duplicateArgument')}
                fields={[
                    {
                        key: 'title',
                        name: 'title',
                        label: t('common.title'),
                        type: 'text',
                        value: title,
                        validators: ['required'],
                    },
                ]}
            />
        </StyArgumentCard>
    );
};

export default ArgumentCard;
